import { Routes, Route, Navigate, Outlet, useLocation, Link } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Calendar, FileText, LayoutDashboard, ExternalLink, History, Dumbbell, Repeat, BookOpen } from "lucide-react";
import { formatDateIT } from "@/lib/date";
import ClientBookingView from "@/components/ClientBookingView";
import ClientPlansPage from "@/pages/ClientPlansPage";
import LessonsHistoryPage from "@/pages/LessonsHistoryPage";
import LoadHistoryPage from "@/pages/LoadHistoryPage";
import RegulationPage from "@/pages/RegulationPage";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const nav = [
  { key: "dash", to: "/app", label: "Il mio profilo", icon: <LayoutDashboard size={16} />, end: true },
  { key: "cal", to: "/app/book", label: "Prenota", icon: <Calendar size={16} /> },
  { key: "plans", to: "/app/plans", label: "Le mie schede", icon: <FileText size={16} /> },
  { key: "history", to: "/app/history", label: "Storico Lezioni", icon: <History size={16} /> },
  { key: "reg", to: "/app/regulation", label: "Regolamento", icon: <BookOpen size={16} /> },
];

export default function ClientDashboard() {
  const [bookings, setBookings] = useState([]);
  const { user, refresh } = useAuth();

  const load = async () => {
    const b = await api.get("/bookings/mine");
    setBookings(b.data);
    await refresh();
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const remaining = (user?.lessons_total || 0) - (user?.lessons_used || 0);

  return (
    <Routes>
      <Route element={<Shell />}>
        <Route index element={<Overview bookings={bookings} remaining={remaining} onChanged={load} />} />
        <Route path="book" element={<BookView remaining={remaining} onChanged={load} />} />
        <Route path="plans" element={<ClientPlansPage />} />
        <Route path="history" element={<LessonsHistoryPage role="client" />} />
        <Route path="regulation" element={<RegulationPage />} />
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Route>
    </Routes>
  );
}

// Hoisted outside so its reference is stable across ClientDashboard re-renders
function Shell() {
  const loc = useLocation();
  const title = loc.pathname.includes("book") ? "Prenota lezione"
    : loc.pathname.includes("plans") ? "Le mie schede"
    : loc.pathname.includes("history") ? "Storico Lezioni"
    : loc.pathname.includes("regulation") ? "Regolamento"
    : "Il mio profilo";
  return <AppShell nav={nav} title={title}><Outlet /></AppShell>;
}

function BookView({ remaining, onChanged }) {
  const { user } = useAuth();
  const [reg, setReg] = useState(null);
  useEffect(() => { api.get("/regulation").then(r => setReg(r.data)).catch(() => {}); }, []);
  const regAccepted = reg && user?.regulation_accepted_version === reg.version;
  if (reg && !regAccepted) {
    return (
      <div>
        <div data-testid="book-blocked-banner" className="border-l-4 border-[#FF3366] bg-[#FF3366]/10 p-4 mb-4">
          <div className="font-heading uppercase tracking-widest text-sm text-white">Prima di prenotare devi visionare e accettare il Regolamento</div>
          <Link to="/app/regulation" data-testid="book-goto-reg" className="btn-primary mt-3 inline-flex items-center gap-2">Vai al Regolamento</Link>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className={`card-surface p-4 mb-4 flex items-center justify-between ${remaining <= 0 ? "border-[#FF3366]/40" : ""}`}>
        <div>
          <div className="label-caps">Lezioni residue</div>
          <div className={`font-heading font-black text-2xl mt-1 ${remaining <= 0 ? "text-[#FF3366]" : ""}`} data-testid="book-remaining-xy">{remaining}</div>
        </div>
        <div className="text-xs text-white/60 max-w-md text-right">
          {remaining <= 0
            ? "Il tuo abbonamento è esaurito. Contatta Sara per rinnovare prima di prenotare nuovi slot."
            : `Puoi prenotare al massimo ${remaining} slot con le lezioni disponibili.`}
        </div>
      </div>
      <ClientBookingView onChanged={onChanged} />
    </div>
  );
}

function Overview({ bookings, remaining, onChanged }) {
  const { user } = useAuth();
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  // Filter cancelled OUT from upcoming (single source of truth for counts)
  const upcoming = bookings.filter(b => b.date >= today && b.status !== "cancelled");
  const past = bookings.filter(b => (b.date < today && b.status !== "cancelled"));
  const low = remaining < 3;

  const cancel = async (b) => {
    const id = typeof b === "string" ? b : b.id;
    // Pre-check: within 24h from lesson start → block with custom message, no API call.
    const startMs = new Date(`${b.date}T${(b.time || "00:00")}:00`).getTime();
    if (!isNaN(startMs) && (startMs - Date.now()) < 24 * 3600 * 1000) {
      alert("Non è possibile annullare la prenotazione a meno di 24 ore dall'inizio della lezione. Per qualsiasi problema contatta Sara.");
      return;
    }
    if (!window.confirm("Annullare questa lezione?")) return;
    try { await api.delete(`/bookings/${id}`); onChanged(); }
    catch (e) { alert(e?.response?.data?.detail || "Errore"); }
  };
  const repeat = async (b) => {
    const raw = window.prompt("Quante settimane vuoi ripetere questa prenotazione (1-52)?", "4");
    const n = parseInt(raw || "0");
    if (!n || n < 1 || n > 52) return;
    try {
      const r = await api.post(`/bookings/${b.id}/repeat`, { occurrences: n, frequency: "weekly" });
      const created = (r.data.created || []).length;
      const skipped = r.data.skipped || [];
      let msg = `Create ${created} prenotazioni ripetute.`;
      if (skipped.length) msg += ` Saltate: ${skipped.map(x => `${formatDateIT(x.date)} (${x.reason})`).join(", ")}`;
      alert(msg);
      onChanged();
    } catch (e) { alert(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="w-full min-w-0 overflow-hidden grid gap-6 lg:grid-cols-12">
      <div className={`card-surface p-6 md:p-8 lg:col-span-5 min-w-0 overflow-hidden ${low ? "border-[#FF3366]/40" : ""}`}>
        <div className="label-caps">Il tuo abbonamento</div>
        <div className={`font-heading font-black text-6xl md:text-7xl mt-3 ${low ? "text-[#FF3366] animate-pulse" : ""}`} data-testid="lessons-xy">{remaining}</div>
        <div className="text-white/50 mt-2 text-sm uppercase tracking-widest">Lezioni residue</div>
        {low && (
          <div className="mt-6 p-4 border border-[#FF3366]/30 text-xs md:text-sm flex items-start gap-3">
            <AlertTriangle size={16} className="text-[#FF3366] mt-0.5 shrink-0" />
            <span className="break-words">Ti restano poche lezioni. Contatta Sara per rinnovare l'abbonamento.</span>
          </div>
        )}
      </div>

      <div className="card-surface p-4 md:p-6 lg:col-span-7 min-w-0 overflow-hidden">
        <div className="label-caps mb-4">Prossime lezioni ({upcoming.length})</div>
        {upcoming.length === 0 && <div className="text-white/40 text-sm py-8">Nessuna lezione in programma. Vai su Prenota per iniziare.</div>}
        <div className="space-y-2">
          {upcoming.map(b => (
            <div key={b.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between border border-white/5 p-3 gap-2 min-w-0">
              <div className="min-w-0">
                <div className="font-heading text-sm md:text-base truncate">{formatDateIT(b.date)} · {b.time}</div>
                <div className="text-[10px] uppercase tracking-widest text-white/40 truncate">{b.service_label}</div>
              </div>
              <div className="flex gap-3 flex-shrink-0">
                <button data-testid={`repeat-my-${b.id}`} onClick={() => repeat(b)} className="text-[#A855F7]/80 hover:text-[#A855F7] text-xs flex items-center gap-1">
                  <Repeat size={12} /> Ripeti
                </button>
                <button data-testid={`cancel-my-${b.id}`} onClick={() => cancel(b)} className="text-[#FF3366]/80 hover:text-[#FF3366] text-xs">Annulla</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {past.length > 0 && (
        <div className="card-surface p-4 md:p-6 lg:col-span-12 min-w-0 overflow-hidden">
          <div className="label-caps mb-4">Storico lezioni ({past.length})</div>
          <div className="grid gap-2 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {past.slice(0, 12).map(b => (
              <div key={b.id} className="border border-white/5 p-3 text-sm text-white/60 flex justify-between gap-2 min-w-0">
                <span className="truncate">{formatDateIT(b.date)} · {b.time}</span>
                <span className="text-[10px] uppercase tracking-widest text-white/40 truncate flex-shrink-0">{b.service_label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Anagrafica + note (read-only content, editable via dialog) */}
      <div className="card-surface p-6 md:p-8 lg:col-span-12 min-w-0 overflow-hidden" data-testid="client-profile-card">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="label-caps">Il mio profilo</div>
          <button data-testid="profile-edit-btn" onClick={() => setEditProfileOpen(true)} className="btn-primary text-xs">Modifica anagrafica</button>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Anagrafica</div>
            <dl className="grid grid-cols-[110px_1fr] gap-y-2 text-sm">
              <dt className="text-white/50">Nome</dt>
              <dd className="text-white" data-testid="profile-first-name">{user?.first_name || "—"}</dd>
              <dt className="text-white/50">Cognome</dt>
              <dd className="text-white" data-testid="profile-last-name">{user?.last_name || "—"}</dd>
              <dt className="text-white/50">Email</dt>
              <dd className="text-white break-all" data-testid="profile-email">{user?.email || "—"}</dd>
              <dt className="text-white/50">Telefono</dt>
              <dd className="text-white" data-testid="profile-phone">{user?.phone || "—"}</dd>
              <dt className="text-white/50">Nascita</dt>
              <dd className="text-white" data-testid="profile-dob">{user?.date_of_birth ? formatDateIT(user.date_of_birth) : "—"}</dd>
            </dl>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Note del trainer</div>
            <div className="text-sm text-white/80 whitespace-pre-wrap italic min-h-[3rem] border border-white/5 p-3" data-testid="profile-notes">
              {user?.notes && user.notes.trim() ? user.notes : "Nessuna nota."}
            </div>
          </div>
        </div>
      </div>
      <EditProfileDialog open={editProfileOpen} onOpenChange={setEditProfileOpen} user={user} onSaved={onChanged} />
    </div>
  );
}

function EditProfileDialog({ open, onOpenChange, user, onSaved }) {
  const { refresh } = useAuth();
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", date_of_birth: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && user) {
      setForm({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        phone: user.phone || "",
        date_of_birth: user.date_of_birth || "",
      });
    }
  }, [open, user]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {};
      if (form.first_name) payload.first_name = form.first_name;
      if (form.last_name) payload.last_name = form.last_name;
      if (form.phone) payload.phone = form.phone;
      if (form.date_of_birth) payload.date_of_birth = form.date_of_birth;
      await api.patch("/auth/me", payload);
      toast.success("Profilo aggiornato");
      await refresh();
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#120B19] border-white/10 text-white rounded-none max-w-md w-[95vw] p-4 sm:p-6" data-testid="profile-edit-dialog">
        <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Modifica anagrafica</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-3">
          <label className="block"><span className="label-caps block mb-1">Nome</span><input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} className="fld2" data-testid="pf-first-name" /></label>
          <label className="block"><span className="label-caps block mb-1">Cognome</span><input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} className="fld2" data-testid="pf-last-name" /></label>
          <label className="block"><span className="label-caps block mb-1">Telefono</span><input required type="tel" pattern="[0-9+\-\s().]{6,25}" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="fld2" data-testid="pf-phone" /></label>
          <label className="block"><span className="label-caps block mb-1">Data di nascita</span><input type="date" value={form.date_of_birth} onChange={e => setForm({ ...form, date_of_birth: e.target.value })} className="fld2" data-testid="pf-dob" /></label>
          <label className="block"><span className="label-caps block mb-1">Email (non modificabile)</span><input value={user?.email || ""} disabled className="fld2 opacity-60 cursor-not-allowed" data-testid="pf-email-readonly" /></label>
          <button disabled={busy} type="submit" data-testid="pf-submit" className="btn-primary mt-2">{busy ? "Salvo…" : "Salva"}</button>
          <style>{`.fld2{width:100%;background:transparent;border:1px solid rgba(255,255,255,0.15);padding:10px 12px;color:#fff;outline:none;font-size:.95rem}.fld2:focus{border-color:#A855F7}`}</style>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MyPlans() {
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/plans").then(r => setPlans(r.data)); }, []);
  const download = async (p) => {
    const r = await api.get(`/plans/${p.id}/file`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };
  return (
    <div>
      <div className="label-caps mb-2">Schede fitness</div>
      <h2 className="font-heading font-black uppercase tracking-tighter text-3xl mb-6">{plans.length} schede assegnate</h2>
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        {plans.map(p => (
          <div key={p.id} className="card-surface p-6 flex flex-col gap-3" data-testid={`my-plan-${p.id}`}>
            <div className="font-heading font-bold uppercase tracking-tight text-lg">{p.title}</div>
            {p.description && <p className="text-sm text-white/60">{p.description}</p>}
            {p.file && (
              <button onClick={() => download(p)} className="flex items-center gap-2 border border-white/10 hover:border-[#A855F7] p-3 text-sm" data-testid={`my-plan-download-${p.id}`}>
                <FileText size={16} className="text-[#A855F7]" /> {p.file.filename}
              </button>
            )}
            {p.images?.length > 0 && (
              <div className="grid grid-cols-3 gap-1">
                {p.images.map((u, i) => (
                  <a key={i} href={u} target="_blank" rel="noreferrer" className="aspect-square block border border-white/10 overflow-hidden">
                    <img src={u} alt="" className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            )}
            {p.links?.map((u, i) => (
              <a key={i} href={u} target="_blank" rel="noreferrer" className="text-xs text-[#A855F7] hover:underline inline-flex items-center gap-1 truncate">
                <ExternalLink size={12} /> {u}
              </a>
            ))}
          </div>
        ))}
        {plans.length === 0 && <div className="card-surface p-12 text-center text-white/40 col-span-full">Nessuna scheda ancora disponibile.</div>}
      </div>
    </div>
  );
}
